import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { logout, sessionRestored } from '@/store/authSlice';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { storage } from '@/utils/storage';
import {
  clearAuthenticationSession,
  refreshAuthenticationSession,
} from '../services/authSession';
import { isRefreshTokenUsable, isTokenUsable } from '../utils/jwtPermissions';

const RequireAuthentication: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const status = useAppSelector((state) => state.auth.status);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const accessTokenIsUsable = isTokenUsable(storage.getAccessToken());
  const refreshTokenIsUsable = isRefreshTokenUsable(storage.getRefreshToken());

  useEffect(() => {
    if (status !== 'authenticated' || accessTokenIsUsable || isRefreshing) return;

    if (!refreshTokenIsUsable) {
      clearAuthenticationSession();
      dispatch(logout());
      return;
    }

    let active = true;
    setIsRefreshing(true);
    void refreshAuthenticationSession()
      .then((session) => {
        if (active) dispatch(sessionRestored(session));
      })
      .catch(() => {
        if (active) dispatch(logout());
      })
      .finally(() => {
        if (active) setIsRefreshing(false);
      });

    return () => {
      active = false;
    };
  }, [accessTokenIsUsable, dispatch, isRefreshing, refreshTokenIsUsable, status]);

  if (status === 'checking' || (status === 'authenticated' && !accessTokenIsUsable && refreshTokenIsUsable)) {
    return (
      <div className="auth-bootstrap" role="status" aria-live="polite">
        <span className="auth-bootstrap-spinner" aria-hidden="true" />
        <span>{t('auth.restoring_session')}</span>
      </div>
    );
  }

  if (status !== 'authenticated' || !accessTokenIsUsable) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  return <Outlet />;
};

export default RequireAuthentication;
